<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, nextTick, reactive, ref, watch } from 'vue'
import { useUser, useNotification, useI18n } from '#imports'
import { useMollie, NEW_CARD } from '~/composables/useMollie'
import type {
    MollieComponentProperties,
    MollieField,
    MollieFieldKind,
    MollieMandate,
} from '~/types/mollie'

const { mollie, init, oneClickPayments, shouldSaveCard, mandates, selectedMandateId, fetchMandates, revokeMandate } = useMollie()
const { isLoggedIn, isGuestSession, user } = useUser()
const { showNotification } = useNotification()
const { t } = useI18n()

/**
 * True while `init()` (config + SDK) and `fetchMandates()` are in flight. The whole card UI
 * is gated behind this so the shopper never sees the new-card form flash and then collapse
 * into the shorter saved-card list once mandates resolve — they see a spinner, then the
 * settled layout (saved-card picker or new-card form) in one step.
 */
const loading = ref(true)

/**
 * One-Click Payment is offered only when enabled gateway-side and the shopper is a
 * logged-in, non-guest customer. Guests and one-click-off never see saved cards or the
 * save opt-in; imitated (support) sessions are treated identically to the customer.
 */
const oneClickEligible = computed(() => {
    return oneClickPayments.value && isLoggedIn.value && !isGuestSession.value;
})

/** Whether to render the saved-card radio list — only when eligible AND at least one mandate exists. */
const showMandateList = computed(() => oneClickEligible.value && mandates.value.length > 0)

/**
 * The embedded Mollie card fields (and the save opt-in) are shown when the shopper is
 * entering a new card: either no saved-card list is rendered at all, or the "New card"
 * radio is selected within it.
 */
const showCardFields = computed(() => !showMandateList.value || selectedMandateId.value === NEW_CARD)

/** Show the "save this card" opt-in only alongside the new-card fields for an eligible shopper. */
const showSaveCard = computed(() => oneClickEligible.value && showCardFields.value)

/** Human-readable label for a saved card: brand · •••• last4 · holder. */
function mandateLabel(details: { cardLabel?: string; cardNumber?: string; cardHolder?: string }): string {
    return [details.cardLabel, details.cardNumber ? `•••• ${details.cardNumber}` : null, details.cardHolder]
        .filter(Boolean)
        .join(' · ')
}

/** Masked descriptor (brand · •••• last4) shown in the delete-confirm dialog. */
function maskedCard(details: { cardLabel?: string; cardNumber?: string }): string {
    return [details.cardLabel, details.cardNumber ? `•••• ${details.cardNumber}` : null]
        .filter(Boolean)
        .join(' · ')
}

/**
 * Delete-a-saved-card confirm flow (A4G-3141). Clicking a row's delete control stages the
 * mandate and opens a lightweight DaisyUI <dialog>; confirming revokes it via useMollie
 * (which reconciles the list + selection) and notifies. No subscription warning — A4G has
 * no subscriptions, so a plain confirm is enough.
 */
const deleteDialog = ref<HTMLDialogElement | null>(null)
const mandateToDelete = ref<MollieMandate | null>(null)
const deleting = ref(false)

function openDeleteDialog(mandate: MollieMandate): void {
    mandateToDelete.value = mandate
    deleteDialog.value?.showModal()
}

function cancelDelete(): void {
    mandateToDelete.value = null
    deleteDialog.value?.close()
}

async function confirmDelete(): Promise<void> {
    const mandate = mandateToDelete.value
    const customerId = user.value?.id
    if (!mandate || !customerId) return
    deleting.value = true
    const ok = await revokeMandate(customerId, mandate.id)
    deleting.value = false
    deleteDialog.value?.close()
    mandateToDelete.value = null
    if (ok) {
        showNotification(t('checkout.mollie.deleteSuccess'), 'success', false)
    } else {
        showNotification(t('checkout.mollie.deleteError'), 'error', true)
    }
}

interface FieldSpec {
    kind: MollieFieldKind
    /** DOM id Mollie mounts its iframe into (matches upstream plugin: no prefix) */
    id: string
    /** i18n key for the field label */
    labelKey: string
}

const FIELDS: FieldSpec[] = [
    { kind: 'cardHolder', id: 'cardHolder', labelKey: 'checkout.mollie.cardHolder' },
    { kind: 'cardNumber', id: 'cardNumber', labelKey: 'checkout.mollie.cardNumber' },
    { kind: 'expiryDate', id: 'expiryDate', labelKey: 'checkout.mollie.expiryDate' },
    { kind: 'verificationCode', id: 'verificationCode', labelKey: 'checkout.mollie.verificationCode' },
]

interface FieldState {
    error: string
    focused: boolean
}

/** Reactive per-field UI state — bound via :class / {{ }} in the template. */
const fieldState = reactive<Record<MollieFieldKind, FieldState>>({
    cardHolder: { error: '', focused: false },
    cardNumber: { error: '', focused: false },
    expiryDate: { error: '', focused: false },
    verificationCode: { error: '', focused: false },
})

const mountedFields = ref<Record<MollieFieldKind, MollieField | null>>({
    cardHolder: null,
    cardNumber: null,
    expiryDate: null,
    verificationCode: null,
})

/**
 * Default style properties passed to `createComponent(name, properties)`.
 * Mirrors `getDefaultProperties()` from the upstream Shopware plugin's
 * `creditcard-components.plugin.js` so visuals stay consistent with the Twig storefront.
 */
function getDefaultProperties(): MollieComponentProperties {
    return {
        styles: {
            base: {
                backgroundColor: '#fff',
                fontSize: '14px',
                padding: '14px 12px',
                '::placeholder': {
                    color: 'rgba(68, 68, 68, 0.2)',
                },
            },
            valid: {},
            invalid: {},
        },
    }
}

/** Create + mount the four Mollie Components iframes into their DOM containers. */
function mountFields(): void {
    const instance = mollie.value
    if (!instance) return
    for (const field of FIELDS) {
        if (mountedFields.value[field.kind]) continue
        const component = instance.createComponent(field.kind, getDefaultProperties())
        component.mount('#' + field.id)
        wireFieldEvents(component, field.kind)
        mountedFields.value[field.kind] = component
    }
}

/** Tear down the four Mollie Components iframes (e.g. when switching to a saved card). */
function unmountFields(): void {
    for (const field of FIELDS) {
        mountedFields.value[field.kind]?.unmount()
        mountedFields.value[field.kind] = null
    }
}

onMounted(async () => {
    await init()
    // Gate the mandate fetch at the call site (ADR-0006 / A4G-3141): only one-click-on,
    // logged-in, non-guest customers list saved cards. fetchMandates never throws.
    if (oneClickEligible.value && user.value?.id) {
        await fetchMandates(user.value.id)
    }
    // Config + mandates resolved — reveal the settled layout in one step (no form→list flash).
    loading.value = false
    await nextTick()
    if (!mollie.value) return
    // Mollie's createComponent().mount('#id') needs the container in the DOM, which only
    // exists while the new-card fields are shown. The containers are already rendered when
    // onMounted runs, so mount straight away if visible; then (re)mount / tear down as the
    // shopper toggles between a saved card and "New card" (a v-if that adds/removes the DOM).
    if (showCardFields.value) mountFields()
    watch(showCardFields, async (visible) => {
        if (visible) {
            await nextTick()
            mountFields()
        } else {
            unmountFields()
        }
    })
})

/**
 * Subscribe to the SDK's own events.
 *
 * NOTE: `change` / `focus` / `blur` are emitted by Mollie's JS object, not by the
 * underlying iframe — `@change` in the template can't catch them. The only way
 * to react is to subscribe via the SDK and push the result into Vue's reactive
 * state, where the template picks it up.
 */
function wireFieldEvents(component: MollieField, kind: MollieFieldKind): void {
    component.addEventListener('change', (event) => {
        const showError = Boolean(event.error && event.touched)
        fieldState[kind].error = showError ? (event.error ?? '') : ''
    })
    component.addEventListener('focus', () => { fieldState[kind].focused = true })
    component.addEventListener('blur', () => { fieldState[kind].focused = false })
}

onBeforeUnmount(() => {
    unmountFields()
})
</script>

<template>
    <div class="mollie-card grid grid-cols-12 gap-3">
        <div
            v-if="loading"
            class="col-span-12 flex items-center justify-center min-h-[8rem] text-base-content-subtle"
            data-cy="checkout-mollie-loading"
        >
            <span class="loading loading-spinner" />
        </div>
        <fieldset v-if="!loading && showMandateList" class="col-span-12 flex flex-col gap-2">
            <div
                v-for="mandate in mandates"
                :key="mandate.id"
                class="flex justify-start items-center gap-3 py-1"
            >
                <label class="label cursor-pointer justify-start gap-3 !p-0">
                    <input
                        v-model="selectedMandateId"
                        type="radio"
                        :value="mandate.id"
                        class="radio radio-sm"
                        :data-cy="`checkout-mollie-mandate-${mandate.id}`"
                    >
                    <span class="label-text">{{ mandateLabel(mandate.details) }}</span>
                </label>
                <button
                    type="button"
                    class="btn btn-sm btn-circle btn-error"
                    :aria-label="$t('checkout.mollie.deleteCard')"
                    :title="$t('checkout.mollie.deleteCard')"
                    :data-cy="`checkout-mollie-delete-${mandate.id}`"
                    @click="openDeleteDialog(mandate)"
                >
                    <MiscIcon name="trash" size="sm" />
                </button>
            </div>
            <label class="label cursor-pointer justify-start gap-3 py-1">
                <input
                    v-model="selectedMandateId"
                    type="radio"
                    :value="NEW_CARD"
                    class="radio radio-sm"
                    data-cy="checkout-mollie-mandate-newCard"
                >
                <span class="label-text">{{ $t('checkout.mollie.newCard') }}</span>
            </label>
        </fieldset>
        <dialog
            ref="deleteDialog"
            class="modal"
            data-cy="checkout-mollie-delete-dialog"
        >
            <div class="modal-box">
                <h3 class="text-lg font-bold">
                    {{ $t('checkout.mollie.deleteTitle') }}
                </h3>
                <p class="py-4">
                    {{ $t('checkout.mollie.deleteBody') }}
                </p>
                <p v-if="mandateToDelete" class="font-semibold">
                    {{ maskedCard(mandateToDelete.details) }}
                </p>
                <div class="modal-action">
                    <button
                        type="button"
                        class="btn btn-ghost"
                        data-cy="checkout-mollie-delete-cancel"
                        @click="cancelDelete()"
                    >
                        {{ $t('checkout.mollie.deleteCancel') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-error"
                        :disabled="deleting"
                        data-cy="checkout-mollie-delete-confirm"
                        @click="confirmDelete()"
                    >
                        <span v-if="deleting" class="loading" />
                        {{ $t('checkout.mollie.deleteConfirm') }}
                    </button>
                </div>
            </div>
        </dialog>
        <template v-if="!loading && showCardFields">
            <div
                v-for="field in FIELDS"
                :key="field.kind"
                class="form-control col-span-12"
                :class="{
                    'lg:col-span-6': field.kind === 'expiryDate' || field.kind === 'verificationCode',
                }"
            >
                <label :for="field.id" class="label py-1">
                    <span class="label-text font-semibold">{{ $t(field.labelKey) }}</span>
                </label>
                <div
                    :id="field.id"
                    class="mollie-field input max-w-none p-0 flex items-stretch [&>.mollie-component]:flex-1 [&_iframe]:block [&_iframe]:h-full"
                    :class="{
                        'pr-3': field.kind === 'cardNumber',
                        'input-error border-error outline-error outline-2': fieldState[field.kind].error.length > 0,
                        'input-primary': fieldState[field.kind].focused && fieldState[field.kind].error.length === 0,
                    }"
                />
                <div
                    :id="field.id + 'Error'"
                    class="mollie-field-error text-error-content text-xs min-h-[1em] mt-1"
                >
                    {{ fieldState[field.kind].error }}
                </div>
            </div>
            <div v-if="showSaveCard" class="form-control col-span-12">
                <label class="label cursor-pointer justify-start gap-3 pt-2 pb-4">
                    <input
                        v-model="shouldSaveCard"
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        data-cy="checkout-mollie-save-card"
                    >
                    <span class="label-text">{{ $t('checkout.mollie.saveCard') }}</span>
                </label>
            </div>
        </template>
    </div>
</template>
